﻿import { useMemo, useState, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { WizardButtons } from '../../Wizard/WizardButtons';
import { GetBeneficiaryInformation } from '../../../../../api/beneficiary';
import { TitledCircularProgress } from '../../feedback/TitledCircularProgress/TitledCircularProgress';
import { Alert, Stack, TextField, Paper, styled, Box } from '@mui/material';
import './beneStyles.css';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Button from '@mui/material/Button';

const ConfirmOwnerCaptureEmail = (props) => {
  const [ownerEmail, setOwnerEmail] = useState('');

  const {
    register,
    trigger,
    formState: { errors },
  } = useForm();

  const BeneBaseData = useQuery({
    queryKey: ['getBeneBase', props.referenceID],
    queryFn: async () =>
      await GetBeneficiaryInformation({ PolicyID: props.referenceID, Source: 4 })(),
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  useEffect(() => {
    if (BeneBaseData.isSuccess && BeneBaseData.data) {
      const data = BeneBaseData.data;
      window.sessionStorage.setItem('currentBene', JSON.stringify(data));

      if (!data.hasIrrevocableBene) {
        if (data.coOwnerNeeded) props.showHiddenHandler(2);

        if (data.isCommunityPropertyState) props.showHiddenHandler(1);
      } else props.hideShownHandler(3);
    }
  }, [BeneBaseData.isSuccess, BeneBaseData.data]);

  const saveEmailValue = () => {
    let data = JSON.parse(window.sessionStorage.getItem('currentBene'));
    data.ownerEmail = ownerEmail;
    window.sessionStorage.setItem('currentBene', JSON.stringify(data));
  };

  const ValidateForm = () => {
    if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.hasIrrevocableBene)
      props.skipHandler(4);
    else if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.hasPOA) props.skipHandler(4);
    else
      trigger().then((d) => {
        clickNext(d);
      });
  };

  const clickNext = (val) => {
    if (val) {
      if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.isCommunityPropertyState) {
        saveEmailValue();
        props.nextHandler();
      } else if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.coOwnerNeeded) {
        saveEmailValue();
        props.skipHandler(2);
      } else {
        saveEmailValue();
        props.skipHandler(3);
      }
    }
  };

  const goBack = () => {
    props.completedHandler();
  };

  const message = useMemo(() => {
    if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.hasIrrevocableBene) {
      return 'an irrevocable beneficiary';
    } else if (BeneBaseData && BeneBaseData.data && BeneBaseData.data.hasPOA) {
      return 'a power of attorney';
    }
    return '';
  }, [BeneBaseData.data]);

  const isBoolean = (ownerEmail) => {
    if (ownerEmail) {
      return true;
    } else {
      return false;
    }
  };

  const renderErrorText = () => {
    if (isBoolean(errors.ownerEmail)) {
      return errors.ownerEmail.message;
    } else {
      return null;
    }
  };

  const { isFetching, isPending, status } = BeneBaseData;

  return (
    <>
      {(isPending || isFetching) && <TitledCircularProgress title="Checking Policy" />}
      {!isFetching && message !== '' && (
        <Alert severity="info">{`This policy has ${message} and must be done via paper`}</Alert>
      )}
      {!isFetching &&
        status !== 'error' &&
        BeneBaseData &&
        BeneBaseData.data &&
        !BeneBaseData.data.hasIrrevocableBene &&
        !BeneBaseData.data.hasPOA && (
          <form>
            <Stack spacing={2}>
              <Alert severity="info">
                Please confirm Owner Info below before capturing Email Address.
              </Alert>
              <BeneFormItem>
                <span className="formLabel">Owner Name:</span>
                <span className="fullwidth">{BeneBaseData.data.ownerName}</span>
              </BeneFormItem>
              <BeneFormItem>
                <span className="formLabel">Owner Last 4:</span>
                <span className="fullwidth">{BeneBaseData.data.ownerLast4}</span>
              </BeneFormItem>
              <BeneFormItem>
                <span className="formLabel">Email Address:</span>
                <TextField
                  {...register('ownerEmail')}
                  error={isBoolean(errors.ownerEmail)}
                  helperText={renderErrorText()}
                  className="fullwidth"
                  value={ownerEmail}
                  onChange={(e) => {
                    setOwnerEmail(e.target.value);
                  }}
                />
              </BeneFormItem>
            </Stack>
          </form>
        )}
      {!isFetching && status !== 'error' && <WizardButtons {...props} nextHandler={ValidateForm} />}
      {!isFetching && status !== 'success' && (
        <>
          <Alert severity="warning">{`${props.referenceID} is not a valid policy number to complete this request. Please go back and select different policy id.`}</Alert>
          <Box textAlign="center" mt="20px">
            <Button
              variant="outlined"
              onClick={() => {
                goBack();
              }}
              startIcon={<ArrowBackIosIcon />}
            >
              Back
            </Button>
          </Box>
        </>
      )}
    </>
  );
};

const decideColor = (theme) => {
  let color = '';
  if (theme.palette.mode === 'dark') {
    color = '#1A2027';
  } else {
    color = '#FFF';
  }
};

const BeneFormItem = styled(Paper)(({ theme }) => ({
  backgroundColor: decideColor(theme),
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: 'left',
  fontWeight: 'bold',
  display: 'flex',
  alignItems: 'flex-start',
  gap: '.25em',
  color: theme.palette.text.secondary,
}));

export { ConfirmOwnerCaptureEmail };
